import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, from, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CommonService } from 'wire-frame';

import { ApiService } from './api.service';
import {
  readBlobErrorMessage,
  resolveReportFileUrl,
} from '../../utils/report-file.util';

@Injectable({
  providedIn: 'root'
})
export class PrescriptionsService {

  private apiUrl: string;

  constructor(private http: HttpClient, private common: CommonService, private apiService: ApiService) {
    this.apiUrl = this.apiService.getBaseUrl();
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`
      })
    };
  }

  getPrescriptions(payload: (string | null) = null) {
    return this.http.get(
      this.apiUrl + '/prescription', this.getAuthHeaders()
    );
  }

  addPrescriptions(payload: any) {
    payload["action"] = "addPatients";
    return this.http.post(this.apiUrl + '/prescription', payload, this.getAuthHeaders());
  }

  updatePrescriptions(payload: any) {
    payload["action"] = "updatePatients";
    return this.http.put(this.apiUrl + '/prescription/' + payload.id, payload, this.getAuthHeaders());
  }

  revisePrescription(id: number, payload: { chief_complaints: string; medicines: string; notes: string }) {
    return this.http.post(this.apiUrl + '/prescription/' + id + '/revise', payload, this.getAuthHeaders());
  }

  deletePrescriptions(payload: any) {
    payload["action"] = "deletePatients";
    return this.http.put(this.common.getBaseUrl() + 'patients.php', payload);
  }

  getLatestPrescription(patientId: number) {
    return this.http.get(this.apiUrl + '/prescription/latest?patient_id=' + patientId, this.getAuthHeaders());
  }

  getLatestBatch(patientIds: number[]) {
    return this.http.post(
      this.apiUrl + '/prescription/latest-batch',
      { patient_ids: patientIds },
      this.getAuthHeaders()
    );
  }

  getPrescriptionsByAppointments(appointmentIds: number[]) {
    return this.http.post(
      this.apiUrl + '/prescription/by-appointments',
      { appointment_ids: appointmentIds },
      this.getAuthHeaders()
    );
  }

  getPrescriptionsByPatientId(patientId: number) {
    return this.http.get(this.apiUrl + '/patients/' + patientId + '/prescriptions', this.getAuthHeaders());
  }

  private toParkHttpParams(params: {
    patient_id: number;
    clinic_id?: number;
    doctor_id?: number;
    date_key?: string;
  }): HttpParams {
    let httpParams = new HttpParams().set('patient_id', String(params.patient_id));
    if (params.date_key) {
      httpParams = httpParams.set('date_key', params.date_key);
    }
    if (params.clinic_id != null && params.clinic_id > 0) {
      httpParams = httpParams.set('clinic_id', String(params.clinic_id));
    }
    if (params.doctor_id != null && params.doctor_id > 0) {
      httpParams = httpParams.set('doctor_id', String(params.doctor_id));
    }
    return httpParams;
  }

  getParkedWork(params: {
    patient_id: number;
    clinic_id?: number;
    doctor_id?: number;
    date_key?: string;
  }) {
    return this.http.get(this.apiUrl + '/prescription/park', {
      ...this.getAuthHeaders(),
      params: this.toParkHttpParams(params),
    });
  }

  upsertParkedWork(payload: any) {
    const body = { ...payload };
    if (!body.clinic_id) {
      delete body.clinic_id;
    }
    if (!body.doctor_id) {
      delete body.doctor_id;
    }
    return this.http.put(this.apiUrl + '/prescription/park', body, this.getAuthHeaders());
  }

  deleteParkedWork(params: {
    patient_id: number;
    clinic_id?: number;
    doctor_id?: number;
    date_key?: string;
  }) {
    return this.http.delete(this.apiUrl + '/prescription/park', {
      ...this.getAuthHeaders(),
      params: this.toParkHttpParams(params),
    });
  }

  uploadReports(formData: FormData) {
    return this.http.post(this.apiUrl + '/reports/upload', formData, this.getAuthHeaders());
  }

  deleteReports(reportId: number) {
    return this.http.delete(this.apiUrl + '/reports/' + reportId, this.getAuthHeaders());
  }

  getReportsByPatientId(patientId: number) {
    return this.http.get(this.apiUrl + '/reports/patient/' + patientId, this.getAuthHeaders());
  }

  resolveReportFileUrl(fileOrUrl: string | { id?: number; url?: string } | null | undefined): string {
    return resolveReportFileUrl(fileOrUrl, this.apiUrl);
  }

  fetchReportBlobById(reportId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/reports/${reportId}/file`, {
      ...this.getAuthHeaders(),
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      switchMap((response) => {
        const blob = response.body;
        const contentType = String(response.headers.get('content-type') || blob?.type || '').toLowerCase();
        if (!blob?.size || response.status >= 400 || contentType.includes('json') || contentType.includes('html')) {
          return from(readBlobErrorMessage(blob, 'Report file unavailable')).pipe(
            switchMap((message) => throwError(() => new Error(message))),
          );
        }
        return of(blob);
      }),
    );
  }

  fetchReportBlob(fileOrUrl: string | { id?: number; url?: string } | null | undefined): Observable<Blob> {
    if (typeof fileOrUrl === 'object' && fileOrUrl?.id) {
      return this.fetchReportBlobById(fileOrUrl.id);
    }

    const url = this.resolveReportFileUrl(fileOrUrl);
    return this.http.get(url, {
      ...this.getAuthHeaders(),
      responseType: 'blob',
    });
  }

}
